import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  People,
  AttachMoney,
  Assessment,
  EmojiEvents,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const SalesDashboard = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLeads: 0,
    convertedClients: 0,
    totalRevenue: 0,
    conversionRate: 0,
    monthlyGoal: 50000,
    currentMonthRevenue: 0,
    topProduct: "Klorum Plan Pro",
    ranking: 3,
  });

  useEffect(() => {
    fetchSalesData();
  }, []);

  const numberLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l.startsWith("en")) return "en-US";
    if (l.startsWith("fr")) return "fr-FR";
    if (l.startsWith("it")) return "it-IT";
    return "es-ES";
  })();

  const formatMoney = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return `$${(0).toLocaleString(numberLocale)}`;
    return `$${n.toLocaleString(numberLocale)}`;
  };

  const fetchSalesData = async () => {
    try {
      setLoading(true);

      // Fetch real data from multiple endpoints
      const [leadsRes, salesRes, metricsRes] = await Promise.all([
        api.get('/leads').catch(() => ({ data: { leads: [] } })),
        api.get('/dashboard/sales-summary').catch(() => ({ data: {} })),
        api.get('/analytics/metrics').catch(() => ({ data: {} }))
      ]);

      const leads = leadsRes.data?.leads || [];
      const sales = salesRes.data || {};
      const metrics = metricsRes.data || {};

      // Calculate stats from real data
      const totalLeads = leads.length;
      const convertedClients = leads.filter(l =>
        l.status === 'converted' || l.status === 'won'
      ).length;
      const totalRevenue = sales.totalSales || leads
        .filter(l => l.status === 'converted' || l.status === 'won')
        .reduce((sum, l) => sum + (l.value || l.deal_value || 0), 0);
      const conversionRate = totalLeads > 0
        ? parseFloat(((convertedClients / totalLeads) * 100).toFixed(1))
        : 0;

      // Get top product from metrics or default
      const topProduct = metrics.topProduct || sales.topProduct || "Klorum Plan";

      setStats({
        totalLeads,
        convertedClients,
        totalRevenue,
        conversionRate,
        monthlyGoal: sales.monthlyGoal || metrics.monthlyGoal || 50000,
        currentMonthRevenue: sales.currentMonthRevenue || totalRevenue,
        topProduct,
        ranking: metrics.ranking || sales.ranking || 0,
      });

    } catch (error) {
      console.error("Error fetching sales data:", error);
      toast.error(i18n.t("salesDashboard.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography color="textSecondary" variant="body2" fontWeight={600}>
            {title}
          </Typography>
          <Icon sx={{ color, fontSize: 28 }} />
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const goalProgress = (stats.currentMonthRevenue / stats.monthlyGoal) * 100;

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
        background: "linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)",
      }}
      elevation={0}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {i18n.t("salesDashboard.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {i18n.t("salesDashboard.subtitle", { name: user?.name || "" })}
        </Typography>
      </Box>

      {loading ? (
        <LinearProgress sx={{ mb: 3 }} />
      ) : (
        <>
          {/* Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("salesDashboard.metrics.totalLeads")}
                value={stats.totalLeads}
                icon={People}
                color="#6366f1"
                subtitle={i18n.t("salesDashboard.metrics.thisMonth")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("salesDashboard.metrics.convertedClients")}
                value={stats.convertedClients}
                icon={TrendingUp}
                color="#10b981"
                subtitle={i18n.t("salesDashboard.metrics.conversionSubtitle", {
                  rate: stats.conversionRate,
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("salesDashboard.metrics.totalRevenue")}
                value={formatMoney(stats.totalRevenue)}
                icon={AttachMoney}
                color="#f59e0b"
                subtitle={i18n.t("salesDashboard.metrics.cumulative")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title={i18n.t("salesDashboard.metrics.ranking")}
                value={`#${stats.ranking}`}
                icon={EmojiEvents}
                color="#ec4899"
                subtitle={i18n.t("salesDashboard.metrics.rankingSubtitle")}
              />
            </Grid>
          </Grid>

          {/* Monthly Goal */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Assessment sx={{ mr: 1, color: "#6366f1" }} />
                    <Typography variant="h6" fontWeight={600}>
                      {i18n.t("salesDashboard.goal.title")}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("salesDashboard.goal.progress", {
                          percent: goalProgress.toFixed(1),
                        })}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(stats.currentMonthRevenue)} / {formatMoney(stats.monthlyGoal)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(goalProgress, 100)}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: "#e5e7eb",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)",
                          borderRadius: 5,
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {goalProgress >= 100
                      ? i18n.t("salesDashboard.goal.reached")
                      : i18n.t("salesDashboard.goal.remaining", {
                          amount: formatMoney(stats.monthlyGoal - stats.currentMonthRevenue),
                        })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {i18n.t("salesDashboard.topProduct.title")}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="h5"
                      color="primary"
                      fontWeight={700}
                      gutterBottom
                    >
                      {stats.topProduct}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("salesDashboard.topProduct.salesThisMonth", {
                        count: stats.convertedClients,
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("salesDashboard.topProduct.conversionRate")}:{" "}
                      <strong style={{ color: "#10b981" }}>
                        {stats.conversionRate}%
                      </strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {i18n.t("salesDashboard.topProduct.avgValue")}:{" "}
                      <strong>
                        {formatMoney(
                          stats.convertedClients > 0
                            ? stats.totalRevenue / stats.convertedClients
                            : 0
                        )}
                      </strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {i18n.t("salesDashboard.quickActions.title")}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 4 },
                    transition: "all 0.3s",
                  }}
                  onClick={() => (window.location.href = "/leads")}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    <People sx={{ fontSize: 40, color: "#6366f1", mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {i18n.t("salesDashboard.quickActions.viewLeads")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 4 },
                    transition: "all 0.3s",
                  }}
                  onClick={() => (window.location.href = "/register-client")}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    <TrendingUp sx={{ fontSize: 40, color: "#10b981", mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {i18n.t("salesDashboard.quickActions.registerClient")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 4 },
                    transition: "all 0.3s",
                  }}
                  onClick={() => (window.location.href = "/commission")}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    <AttachMoney sx={{ fontSize: 40, color: "#f59e0b", mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {i18n.t("salesDashboard.quickActions.commissions")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { boxShadow: 4 },
                    transition: "all 0.3s",
                  }}
                  onClick={() => (window.location.href = "/prompts")}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    <Assessment sx={{ fontSize: 40, color: "#ec4899", mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {i18n.t("salesDashboard.quickActions.aiSales")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SalesDashboard;
